/* prettier-ignore-file */
/**
 * Quick rental cost estimator, openable from any step of the booking wizard.
 *
 * Deliberately ignores inventory: this is a "what would this cost" tool, so every
 * catalog product can be dialled up freely and nothing is capped by what happens
 * to be free on a given date. Real availability is enforced by the wizard.
 *
 * Holds its own state, reads nothing from the booking draft and writes nothing
 * back, so opening and closing it cannot disturb wizard progress and its total
 * is never a quote for the order in progress.
 *
 * Mileage goes through the getDistance callable -- the same path a real booking
 * takes -- so an estimate for a given address matches what that customer is
 * charged at checkout. That means the shop origin is getDistance's, and is not
 * editable here; an arbitrary origin would produce a number the booking would
 * never agree with.
 */

import React, { useState, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  TextInput,
  ActivityIndicator,
} from "react-native";
import { getFunctions, httpsCallable } from "firebase/functions";
import GoogleAddressInput from "./GoogleAddressInput";
import { app } from "../firebase.config";
import { useProducts, useSettings } from "../stores";
import { formatCentsAsUSD } from "../utils/formatting";
import { computeRentalQuote, readPricingSettings } from "../utils/rentalPricing";

const functions = getFunctions(app);

const RentalCalculatorModal = ({ visible, onClose }) => {
  const products1 = useProducts((state) => state.products1) || [];
  const products2 = useProducts((state) => state.products2) || [];
  const settings = useSettings((state) => state.settings);
  const cfg = readPricingSettings(settings);

  const [days, setDays] = useState(1);
  const [quantities1, setQuantities1] = useState({});
  const [quantities2, setQuantities2] = useState({});
  const [delivery, setDelivery] = useState(false);
  const [homePickup, setHomePickup] = useState(false);
  const [customerAddress, setCustomerAddress] = useState("");
  const [miles, setMiles] = useState(null);
  const [milesLoading, setMilesLoading] = useState(false);
  const [milesError, setMilesError] = useState(null);

  // Nothing is seeded from the booking draft on purpose. This is a clean-slate
  // estimator: it must answer "what would this cost" from scratch, not quote the
  // order in progress. Inheriting days/delivery/miles used to make it price
  // transportation and a duration the user had not entered here, which read as
  // the estimate disagreeing with the wizard.

  const needsMileage = delivery || homePickup;

  // Per-bike add-ons multiply by this, so they can't be priced before a bike is
  // chosen — same gate StepProducts applies.
  const totalBikes = useMemo(
    () => Object.values(quantities1).reduce((sum, qty) => sum + (qty || 0), 0),
    [quantities1]
  );

  // Synthesize a date range from the day count so duration-discount tiers, which
  // are keyed on days, resolve exactly as they will in the real booking.
  const { startDate, endDate } = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + Math.max(1, days));
    return { startDate: start, endDate: end };
  }, [days]);

  const product1Items = useMemo(
    () =>
      Object.entries(quantities1)
        .filter(([, qty]) => qty > 0)
        .map(([id, quantity]) => ({ id, quantity })),
    [quantities1]
  );

  const product2Items = useMemo(
    () =>
      Object.entries(quantities2)
        .filter(([, qty]) => qty > 0)
        .map(([id, quantity]) => ({ id, quantity })),
    [quantities2]
  );

  const quote = useMemo(
    () =>
      computeRentalQuote({
        startDate,
        endDate,
        product1: product1Items,
        product2: product2Items,
        products1Catalog: products1,
        products2Catalog: products2,
        settings,
        // Price the legs only once a distance exists, so the total matches the
        // "transportation not included yet" hint instead of silently including
        // the loading fee.
        delivery: delivery && miles != null,
        homePickup: homePickup && miles != null,
        miles,
      }),
    [startDate, endDate, product1Items, product2Items, products1, products2, settings, delivery, homePickup, miles]
  );

  // Deliberately the same call the real booking makes (StepTransportation.js:126),
  // including the round to 1 decimal. Distance is the one quote input that could
  // otherwise differ, and an estimate is only useful if it matches what the
  // customer is actually charged -- so the shop origin is whatever getDistance
  // uses, not something entered here.
  const handleCalculateMileage = async () => {
    if (!customerAddress.trim()) {
      setMilesError("Enter your address first.");
      return;
    }
    setMilesLoading(true);
    setMilesError(null);
    try {
      const getDistanceFn = httpsCallable(functions, "getDistance");
      const result = await getDistanceFn({ fullAddress: customerAddress.trim() });
      const resolved = result.data?.distanceMiles ?? result.data?.miles;
      if (resolved == null) throw new Error("No distance returned");
      setMiles(Number(Number(resolved).toFixed(1)));
    } catch (error) {
      console.error("❌ Calculator mileage error:", error);
      setMilesError("Could not calculate distance. Check the address.");
      setMiles(null);
    } finally {
      setMilesLoading(false);
    }
  };

  const adjustQty = (setter, id, delta) => {
    setter((prev) => {
      const next = Math.max(0, (prev[id] || 0) + delta);
      const updated = { ...prev };
      if (next === 0) delete updated[id];
      else updated[id] = next;
      return updated;
    });
  };

  // Bike quantity changes also police per-bike add-ons: when the last bike is
  // removed, a checked per-bike add-on would sit priced at $0 and spring back
  // when a bike returns — drop it instead (same behavior as the web port).
  const adjustBikeQty = (id, delta) => {
    const next = Math.max(0, (quantities1[id] || 0) + delta);
    const updated = { ...quantities1 };
    if (next === 0) delete updated[id];
    else updated[id] = next;
    setQuantities1(updated);
    const remainingBikes = Object.values(updated).reduce((sum, qty) => sum + (qty || 0), 0);
    if (remainingBikes === 0) {
      setQuantities2((prev) => {
        const perBikeIds = products2
          .filter((p) => (p.option || "per-booking") === "per-bike")
          .map((p) => p.id);
        if (!perBikeIds.some((pid) => prev[pid])) return prev;
        const cleaned = { ...prev };
        perBikeIds.forEach((pid) => delete cleaned[pid]);
        return cleaned;
      });
    }
  };

  const handleReset = () => {
    setDays(1);
    setQuantities1({});
    setQuantities2({});
    setDelivery(false);
    setHomePickup(false);
    setCustomerAddress("");
    setMiles(null);
    setMilesError(null);
  };

  const renderQtyRow = (product, quantities, setter, priceSuffix, adjust) => {
    const qty = quantities[product.id] || 0;
    const change = adjust || ((id, delta) => adjustQty(setter, id, delta));
    return (
      <View key={product.id} style={styles.productRow}>
        <View style={styles.productInfo}>
          <Text style={styles.productName}>{product.name}</Text>
          <Text style={styles.productPrice}>
            {formatCentsAsUSD(product.price || 0)}
            {priceSuffix}
          </Text>
        </View>
        <View style={styles.qtyControls}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.qtyButton, qty === 0 && styles.qtyButtonDisabled]}
            onPress={() => change(product.id, -1)}
            disabled={qty === 0}
          >
            <Text style={styles.qtyButtonText}>−</Text>
          </TouchableOpacity>
          <Text style={styles.qtyValue}>{qty}</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.qtyButton}
            onPress={() => change(product.id, 1)}
          >
            <Text style={styles.qtyButtonText}>+</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Per-bike add-ons are a yes/no choice applied to every bike in the order —
  // the wizard books them as a boolean (StepProducts), so a quantity stepper
  // here could quote totals no real booking can match. Priced price × bikes.
  const renderPerBikeRow = (product) => {
    const checked = (quantities2[product.id] || 0) > 0;
    const blocked = totalBikes === 0;
    const toggle = () => {
      if (blocked) return;
      setQuantities2((prev) => {
        const updated = { ...prev };
        if (checked) delete updated[product.id];
        else updated[product.id] = 1;
        return updated;
      });
    };
    return (
      <TouchableOpacity
        key={product.id}
        style={[styles.checkboxRow, blocked && styles.perBikeRowDisabled]}
        onPress={toggle}
        disabled={blocked}
      >
        <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
          {checked && <Text style={styles.checkboxMark}>✓</Text>}
        </View>
        <View style={styles.productInfo}>
          <Text style={styles.productName}>{product.name}</Text>
          <Text style={styles.productPrice}>
            {formatCentsAsUSD(product.price || 0)} / bike
            {blocked
              ? " · pick a bike first"
              : ` · ${totalBikes} ${totalBikes === 1 ? "bike" : "bikes"}`}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const summaryRow = (label, valueCents, options = {}) => (
    <View style={styles.summaryRow}>
      <Text style={[styles.summaryLabel, options.muted && styles.summaryMuted]}>{label}</Text>
      <Text
        style={[
          styles.summaryValue,
          options.muted && styles.summaryMuted,
          options.discount && styles.summaryDiscount,
        ]}
      >
        {options.discount ? `−${formatCentsAsUSD(Math.abs(valueCents))}` : formatCentsAsUSD(valueCents)}
      </Text>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>🧮 Rental Cost Calculator</Text>
            <TouchableOpacity dataSet={{ hover: "lift" }} onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.headerHint}>
            A quick estimate. Availability is not checked here — pick your actual dates and items in
            the booking steps.
          </Text>

          <ScrollView style={styles.body}>
            {/* ---- Duration ---- */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>How many days?</Text>
              <View style={styles.daysRow}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.qtyButton, days <= 1 && styles.qtyButtonDisabled]}
                  onPress={() => setDays((d) => Math.max(1, d - 1))}
                  disabled={days <= 1}
                >
                  <Text style={styles.qtyButtonText}>−</Text>
                </TouchableOpacity>
                <TextInput
                  style={styles.daysInput}
                  value={String(days)}
                  keyboardType="number-pad"
                  onChangeText={(text) => {
                    const parsed = parseInt(text.replace(/\D/g, ""), 10);
                    setDays(Number.isFinite(parsed) && parsed > 0 ? parsed : 1);
                  }}
                />
                <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.qtyButton} onPress={() => setDays((d) => d + 1)}>
                  <Text style={styles.qtyButtonText}>+</Text>
                </TouchableOpacity>
                <Text style={styles.daysLabel}>{days === 1 ? "day" : "days"}</Text>
              </View>
            </View>

            {/* ---- Products ---- */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{cfg.product1Title}</Text>
              {products1.length === 0 ? (
                <Text style={styles.emptyText}>No products configured.</Text>
              ) : (
                products1.map((p) =>
                  renderQtyRow(p, quantities1, setQuantities1, " / day", adjustBikeQty)
                )
              )}
            </View>

            {products2.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{cfg.product2Title}</Text>
                {products2.map((p) => {
                  const option = p.option || "per-booking";
                  if (option === "per-bike") return renderPerBikeRow(p);
                  const suffix = option === "user-selection" ? " / day" : "";
                  return renderQtyRow(p, quantities2, setQuantities2, suffix);
                })}
              </View>
            )}

            {/* ---- Transportation ---- */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Transportation</Text>

              <TouchableOpacity style={styles.checkboxRow} onPress={() => setDelivery((v) => !v)}>
                <View style={[styles.checkbox, delivery && styles.checkboxChecked]}>
                  {delivery && <Text style={styles.checkboxMark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Delivery to me</Text>
              </TouchableOpacity>

              <TouchableOpacity style={styles.checkboxRow} onPress={() => setHomePickup((v) => !v)}>
                <View style={[styles.checkbox, homePickup && styles.checkboxChecked]}>
                  {homePickup && <Text style={styles.checkboxMark}>✓</Text>}
                </View>
                <Text style={styles.checkboxLabel}>Pickup from me at the end</Text>
              </TouchableOpacity>

              {needsMileage && (
                <View style={styles.mileageBlock}>
                  <Text style={styles.fieldLabel}>Your address</Text>
                  <GoogleAddressInput
                    value={customerAddress}
                    onChangeText={(text) => {
                      setCustomerAddress(text);
                      setMiles(null);
                    }}
                    onAddressSelected={(address) => {
                      setCustomerAddress(address);
                      setMiles(null);
                    }}
                    placeholder="Start typing your address..."
                    style={styles.addressInput}
                  />

                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.mileageButton, milesLoading && styles.mileageButtonDisabled]}
                    onPress={handleCalculateMileage}
                    disabled={milesLoading}
                  >
                    {milesLoading ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.mileageButtonText}>📍 Calculate mileage</Text>
                    )}
                  </TouchableOpacity>

                  {milesError && <Text style={styles.errorText}>{milesError}</Text>}
                  {miles != null && (
                    <Text style={styles.milesText}>
                      {miles} miles each way · {formatCentsAsUSD(quote.oneWayCents)} per trip
                    </Text>
                  )}
                  {miles == null && !milesError && (
                    <Text style={styles.hintText}>
                      Enter both addresses to include mileage in the estimate.
                    </Text>
                  )}
                </View>
              )}
            </View>

            {/* ---- Estimate ---- */}
            <View style={[styles.section, styles.estimateSection]}>
              <Text style={styles.sectionTitle}>Estimate</Text>

              {summaryRow(`${cfg.product1Title} (${quote.days} ${quote.days === 1 ? "day" : "days"})`, quote.product1Cents)}
              {quote.product2Cents > 0 && summaryRow(cfg.product2Title, quote.product2Cents)}

              {quote.durationDiscountCents > 0 &&
                summaryRow(
                  quote.durationDiscountName || `Duration discount (${quote.durationDiscountPercent}%)`,
                  quote.durationDiscountCents,
                  { discount: true }
                )}

              {quote.deliveryCostCents > 0 && summaryRow("Delivery", quote.deliveryCostCents)}
              {quote.pickupCostCents > 0 && summaryRow("Home pickup", quote.pickupCostCents)}

              {quote.transportationDiscountCents > 0 &&
                summaryRow(
                  quote.transportationDiscountName ||
                    `Transportation discount (${quote.transportationDiscountPercent}%)`,
                  quote.transportationDiscountCents,
                  { discount: true }
                )}

              {quote.platformFeeCents > 0 && summaryRow("Booking fee", quote.platformFeeCents, { muted: true })}
              {quote.salesTaxCents > 0 &&
                summaryRow(`Sales tax (${quote.salesTaxPercent}%)`, quote.salesTaxCents, { muted: true })}
              {quote.cardProcessingFeeCents > 0 &&
                summaryRow(
                  `Card processing (${quote.cardProcessingFeePercent}%)`,
                  quote.cardProcessingFeeCents,
                  { muted: true }
                )}

              <View style={styles.totalDivider} />
              <View style={styles.summaryRow}>
                <Text style={styles.totalLabel}>Estimated total</Text>
                <Text style={styles.totalValue}>{formatCentsAsUSD(quote.finalTotalCents)}</Text>
              </View>

              {needsMileage && miles == null && (
                <Text style={styles.hintText}>
                  Transportation is not included yet — calculate mileage above.
                </Text>
              )}
            </View>

            <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.resetButton} onPress={handleReset}>
              <Text style={styles.resetButtonText}>Reset calculator</Text>
            </TouchableOpacity>

            <View style={styles.bottomSpacer} />
          </ScrollView>

          <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.doneButton} onPress={onClose}>
            <Text style={styles.doneButtonText}>Done</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "flex-end",
  },
  sheet: {
    backgroundColor: "#F6F8F7",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "92vh",
    width: "100%",
    maxWidth: 800,
    alignSelf: "center",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  headerTitle: { fontSize: 18, fontWeight: "bold", color: "#2E3A38" },
  closeButton: { padding: 8 },
  closeButtonText: { fontSize: 18, color: "#5C6B68" },
  headerHint: { fontSize: 12, color: "#5C6B68", paddingHorizontal: 16, paddingBottom: 12, paddingTop: 4 },
  body: { paddingHorizontal: 16 },

  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
  },
  estimateSection: { borderWidth: 2, borderColor: "#0E7C6B" },
  sectionTitle: { fontSize: 15, fontWeight: "bold", color: "#2E3A38", marginBottom: 12 },
  emptyText: { fontSize: 13, color: "#8A9794" },

  daysRow: { flexDirection: "row", alignItems: "center" },
  daysInput: {
    width: 56,
    height: 36,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    marginHorizontal: 8,
    textAlign: "center",
    fontSize: 16,
    color: "#2E3A38",
  },
  daysLabel: { marginLeft: 10, fontSize: 14, color: "#5C6B68" },

  productRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  productInfo: { flex: 1, paddingRight: 12 },
  productName: { fontSize: 14, color: "#2E3A38", fontWeight: "500" },
  productPrice: { fontSize: 12, color: "#5C6B68", marginTop: 2 },
  qtyControls: { flexDirection: "row", alignItems: "center" },
  qtyButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  qtyButtonDisabled: { backgroundColor: "#DDE3E2" },
  qtyButtonText: { color: "#FFFFFF", fontSize: 18, fontWeight: "bold", lineHeight: 20 },
  qtyValue: { minWidth: 40, textAlign: "center", fontSize: 15, fontWeight: "600", color: "#2E3A38" },

  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    minHeight: 48,
    cursor: "pointer",
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: "#DDE3E2",
    marginRight: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxChecked: { backgroundColor: "#0E7C6B", borderColor: "#0E7C6B" },
  checkboxMark: { color: "#FFFFFF", fontSize: 14, fontWeight: "bold" },
  checkboxLabel: { fontSize: 14, color: "#2E3A38" },
  perBikeRowDisabled: { opacity: 0.45 },

  mileageBlock: { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: "#EDF1F0" },
  fieldLabel: { fontSize: 12, color: "#5C6B68", marginBottom: 4, marginTop: 8 },
  addressInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 10,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
  },
  mileageButton: {
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    paddingVertical: 12,
    alignItems: "center",
    marginTop: 12,
    cursor: "pointer",
  },
  mileageButtonDisabled: { backgroundColor: "#9FC5BE" },
  mileageButtonText: { color: "#FFFFFF", fontSize: 14, fontWeight: "600" },
  milesText: { fontSize: 13, color: "#1F8A5B", marginTop: 8, fontWeight: "600" },
  errorText: { fontSize: 12, color: "#C4453D", marginTop: 8 },
  hintText: { fontSize: 12, color: "#8A9794", marginTop: 8 },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 5,
  },
  summaryLabel: { fontSize: 13, color: "#2E3A38", flex: 1, paddingRight: 8 },
  summaryValue: { fontSize: 13, color: "#2E3A38", fontWeight: "500" },
  summaryMuted: { color: "#5C6B68" },
  summaryDiscount: { color: "#1F8A5B" },
  totalDivider: { height: 1, backgroundColor: "#DDE3E2", marginVertical: 10 },
  totalLabel: { fontSize: 16, fontWeight: "bold", color: "#2E3A38" },
  totalValue: { fontSize: 18, fontWeight: "bold", color: "#0E7C6B" },

  resetButton: { alignItems: "center", paddingVertical: 10 },
  resetButtonText: { fontSize: 13, color: "#5C6B68", textDecorationLine: "underline" },
  bottomSpacer: { height: 8 },

  doneButton: {
    backgroundColor: "#0E7C6B",
    margin: 16,
    marginTop: 8,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    cursor: "pointer",
  },
  doneButtonText: { color: "#FFFFFF", fontSize: 16, fontWeight: "600" },
});

export default RentalCalculatorModal;
